import type { Cadence, PickerOption } from './plans';

// The rules behind the picker's billing-cycle step (the variant arm of
// `symphonyBillingCycleStepWeb`). Pure and import-free — `./plans` is types-only here, erased
// at compile — so a unit test can exercise them without dragging the app's dependency graph in (see
// the jest note in CLAUDE.md: a test that reaches common-lib's `rpc` barrel fails to RUN in CI while
// passing locally).
//
// They live here rather than in the component for the same reason `pricedCadences` lives in `plans`:
// each is a decision over the catalog, and the screen has no render test to pin it with.

/** One cycle the buyer may pick, with the price the picker charges for it. */
export interface CycleOffer {
  cadence: Cadence;
  /** The real charge at this cadence: a full year for `yearly`, one month for `monthly`. */
  amount: number;
  /** The per-month figure to SHOW — yearly divided by 12, so it reads against the monthly row. */
  perMonth: number;
}

/**
 * The cycles this option actually prices, yearly first.
 *
 * An unpriced cadence is `undefined`, never `0` (see `PickerOption.monthlyAmount`), so this tests
 * null-ness rather than truthiness — a genuinely free-priced option must still be offered rather than
 * silently dropped. Order is fixed rather than sorted by price: yearly leads because it is the arm the
 * control toggle opens on, and a row order that moved with the numbers would put a different option
 * under the buyer's cursor in different markets.
 */
export function cycleOffers(option: PickerOption): CycleOffer[] {
  const offers: CycleOffer[] = [];
  if (option.yearlyAmount != null) {
    offers.push({ cadence: 'yearly', amount: option.yearlyAmount, perMonth: Math.round(option.yearlyAmount / 12) });
  }
  if (option.monthlyAmount != null) {
    const monthly = Math.round(option.monthlyAmount);
    offers.push({ cadence: 'monthly', amount: option.monthlyAmount, perMonth: monthly });
  }
  return offers;
}

/**
 * The cycle the step opens on: yearly when priced, else whatever is.
 *
 * Yearly is deliberately the default, matching the control toggle's own opening state
 * (`selectedCadence` in `SymphonyPricing`). If the variant quietly opened on monthly the experiment
 * would be measuring two changes at once — the extra step AND a cheaper-looking first price — and
 * neither result could be read.
 */
export function defaultCycle(option: PickerOption): Cadence {
  return option.yearlyAmount != null ? 'yearly' : 'monthly';
}

/**
 * What a year of yearly billing saves over twelve monthly charges, or `undefined` when there is
 * nothing honest to claim.
 *
 * The ANNUAL saving, not a monthly one: the row is offering a year, so the number beside it has to be
 * the money that decision saves. `undefined` whenever either cadence is unpriced, or when yearly is
 * not actually cheaper — a market where the two happen to match would otherwise wear a "Save 0" badge,
 * which is an invented reason to pick one.
 */
export function yearlySaving(option: PickerOption): number | undefined {
  const { yearlyAmount, monthlyAmount } = option;
  if (yearlyAmount == null || monthlyAmount == null) return undefined;
  const saving = monthlyAmount * 12 - yearlyAmount;
  return saving > 0 ? saving : undefined;
}

/**
 * Is this option the buyer's CURRENT plan, for the purposes of the card's "you are here" chrome —
 * the accent ring, the CURRENT PLAN badge, the ⋯ menu that holds Cancel, and the top-up button?
 *
 * The picker normally answers this at the cadence ON SCREEN, so the same plan's other cadence reads
 * as an upgrade/downgrade offer rather than as the plan you already own. That is right while a
 * cadence toggle exists, because the buyer can move the screen to their own cadence.
 *
 * **In the variant arm there is no toggle**, and the cards are pinned to yearly. Asking the
 * cadence-sensitive question there is not merely imprecise — it is unanswerable, and it answered NO
 * for every monthly subscriber: their own card stopped reading as current, which took the ⋯ menu
 * with it. Cancelling a subscription lives ONLY on this page (see the note in
 * `components/settings/shared/settings-queries.ts`), and both native apps open this page, so that
 * combination left a paying monthly customer with no way to cancel on any surface at all.
 *
 * So when the cycle has not been asked yet, ownership is cadence-AGNOSTIC: you own the plan, and
 * which cycle you own it at is a question this screen is deliberately not putting to you.
 */
export function ownsOption(
  subscribedCadence: Cadence | undefined,
  displayedCadence: Cadence,
  cycleUnasked: boolean,
): boolean {
  if (subscribedCadence === undefined) return false;
  return cycleUnasked || subscribedCadence === displayedCadence;
}

/**
 * Can this option be bought from the card?
 *
 * With a toggle on screen the answer is about the cadence shown: never let a buyer check out a
 * cadence we could not price, because the server would still resolve a real Stripe price and charge
 * for it. With the cycle unasked, the card is not selling a cadence at all — it hands the option to
 * the step, which offers only the cycles that ARE priced. So an option priced monthly-only stays
 * buyable rather than rendering a dead "Not available yearly" CTA the arm has no toggle to escape.
 */
export function optionPurchasable(
  option: PickerOption | undefined,
  displayedCadence: Cadence,
  cycleUnasked: boolean,
): boolean {
  if (!option) return false;
  if (cycleUnasked) return cycleOffers(option).length > 0;
  return (displayedCadence === 'yearly' ? option.yearlyAmount : option.monthlyAmount) != null;
}

/**
 * Which cadence a plan CARD should price itself at.
 *
 * Normally the one on screen — the toggle is the buyer's own choice and the cards follow it. Two
 * things go wrong when the cycle has not been asked yet and the cards are pinned to yearly:
 *
 * - **A subscriber's own card would quote a price they do not pay.** `ownsOption` deliberately makes
 *   ownership cadence-agnostic (without it a monthly subscriber loses the ⋯ menu, and with it the
 *   only "Cancel plan" in the product). But that means a MONTHLY subscriber's card is marked
 *   "Current plan" — a non-clickable status, not a CTA — while the price above it is the yearly
 *   figure divided by twelve, with the "Billed … / year" qualifier suppressed because this arm has
 *   not asked about cycles. So it reads "$80 / month · Current plan" to someone paying $100. A card
 *   that names itself the buyer's plan has to state the buyer's price.
 * - **An option priced at only one cadence would advertise the wrong one.** `optionPurchasable`
 *   already ignores the displayed cadence here, so the CTA is live; without the same treatment the
 *   price slot beside it renders the literal "Not available yearly".
 *
 * So: what you own if you own it, else whatever this option is actually priced at, preferring
 * yearly — which is the arm's own default and the figure the step opens on.
 */
export function cardCadence(
  option: PickerOption | undefined,
  displayedCadence: Cadence,
  cycleUnasked: boolean,
  ownedCadence: Cadence | undefined,
): Cadence {
  if (!cycleUnasked) return displayedCadence;
  if (ownedCadence) return ownedCadence;
  if (!option) return displayedCadence;
  if (option.yearlyAmount != null) return 'yearly';
  return option.monthlyAmount != null ? 'monthly' : displayedCadence;
}

/**
 * What paying monthly costs a buyer over paying yearly, for the SAME credits.
 *
 * Both cadences grant the same allowance every month (`AgentPlan.monthlyCredits` is per month either
 * way, and `yearlyCredits` is just twelve of them), so the entire difference between the two rows is
 * what those credits cost. That is why this can be stated as a rate rather than as a discount off a
 * total — and why the monthly row is able to argue against itself without quoting an annual sum the
 * screen deliberately does not show.
 *
 * The rate is quoted per THOUSAND credits, not per credit. A single credit is worth a fraction of a
 * cent on every plan and in every market — $0.0083 on Max — and at that scale two decimals collapse
 * the very gap the line exists to show: "$0.008 vs $0.007" reads as 14% apart while the percent
 * beside it says 25%. Per thousand, the same two figures are $8.33 and $6.67: two decimals, and
 * exactly the stated 25% apart. It also survives the zero-decimal currencies, where a per-credit
 * figure has no digits at all to land in (¥1.50 on Max is ¥1,500 per thousand).
 */
export interface MonthlyPremium {
  /** How much more monthly credits cost than yearly ones, in whole percent. */
  percent: number;
  /** What 1,000 credits cost on monthly, in the plan's currency. */
  monthlyPerThousand: number;
  /** The same thousand credits on yearly. */
  yearlyPerThousand: number;
}

/**
 * The monthly row's counter-argument, or `undefined` when there is nothing honest to claim.
 *
 * `undefined` in four cases, and each one is a state this screen genuinely reaches:
 *
 * - **No yearly price.** A monthly-only market (India — see `pricedCadences`) offers the monthly row
 *   as the ONLY option, so nothing is being given up by taking it. The row falls back to the plain
 *   "what you get" list there rather than warning a buyer off the one thing they can buy.
 * - **No monthly price**, symmetrically — there is no monthly row to write on.
 * - **A plan that grants no credits.** The rate is a division by zero, and a "per 1,000 credits"
 *   line about a plan with no credits says nothing.
 * - **Yearly is not actually cheaper per month.** The same rule `yearlySaving` applies to its badge:
 *   a market where the two happen to match would otherwise wear an invented "0% more", and one where
 *   yearly costs MORE would have this row recommending the expensive option.
 */
export function monthlyPremium(option: PickerOption): MonthlyPremium | undefined {
  const { monthlyAmount, yearlyAmount, credits } = option;
  if (monthlyAmount == null || yearlyAmount == null || credits <= 0) return undefined;
  const yearlyPerMonth = yearlyAmount / 12;
  // Tested for positivity rather than null-ness: a genuinely free-priced yearly (allowed — see
  // `PickerOption.monthlyAmount`) would make the ratio below Infinity, and "Infinity% more" is what
  // would render.
  if (yearlyPerMonth <= 0) return undefined;
  const percent = Math.round((monthlyAmount / yearlyPerMonth - 1) * 100);
  if (percent < 1) return undefined;
  return {
    percent,
    monthlyPerThousand: (monthlyAmount / credits) * 1000,
    yearlyPerThousand: (yearlyPerMonth / credits) * 1000,
  };
}
